"use client";

import { FC, useCallback, useEffect, useRef } from "react";
import { Content } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import { PrismicNextImage } from "@prismicio/next";
import aboutHeroBottomCardShape from "@/assets/aboutHeroBottomCardShape.svg";
import aboutHeroTopCardShape from "@/assets/aboutHeroTopCardShape.svg";
import aboutHeroContainerLeftShape from "@/assets/aboutHeroContainerLeftShape.svg";
import aboutHeroContainerRightShape from "@/assets/aboutHeroContainerRightShape.svg";
import noise from "@/assets/noise.png";
import Image from "next/image";
import { MotionPrismicNextImage } from "@/components/ui/motion-prismic-next-image";
import { animate } from "framer-motion";

/**
 * Props for `AboutHeroSection`.
 */
export type AboutHeroSectionProps =
  SliceComponentProps<Content.AboutHeroSectionSlice>;

/**
 * Component for "AboutHeroSection" Slices.
 */
const AboutHeroSection: FC<AboutHeroSectionProps> = ({ slice }) => {
  const topCardValueRef = useRef<HTMLDivElement | null>(null);
  const bottomCardValueRef = useRef<HTMLDivElement | null>(null);

  const animateFrom0ToN = useCallback(
    (
      ref: React.MutableRefObject<HTMLDivElement | null>,
      targetNum: number,
      duration?: number
    ) => {
      const node = ref.current;

      if (node) {
        node.textContent = "0+";

        const controls = animate(0, targetNum, {
          duration: duration ?? 1,
          ease: "easeOut",
          onUpdate: (latestValue) => {
            node.textContent = `${latestValue.toFixed(0)}+`;
          },
        });

        return () => controls.stop();
      }
    },
    []
  );

  useEffect(() => {
    animateFrom0ToN(topCardValueRef, slice.primary.top_card_value || 0);
    animateFrom0ToN(
      bottomCardValueRef,
      slice.primary.bottom_card_value || 0,
      2
    );
  }, [
    animateFrom0ToN,
    slice.primary.top_card_value,
    slice.primary.bottom_card_value,
  ]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#EBF7FF] relative"
    >
      <Image
        src={aboutHeroContainerLeftShape.src}
        alt="left shape"
        width={806.89}
        height={749.22}
        className="absolute left-0 h-full w-auto pointer-events-none select-none"
        priority
        fetchPriority="high"
      />
      <Image
        src={aboutHeroContainerRightShape.src}
        alt="right shape"
        width={710.89}
        height={657.36}
        className="absolute right-0 h-full w-auto pointer-events-none select-none"
        priority
        fetchPriority="high"
      />
      <Bounded as="div" className="py-20 relative container mx-auto">
        <div className="flex items-center flex-wrap flex-col md:flex-row w-full gap-2.5 mx-auto">
          <MotionPrismicNextImage
            field={slice.primary.left_image}
            width={635}
            height={508}
            className="rounded-xl w-full xl:w-[635px] h-[508px] object-cover object-top-left"
            initial={{
              opacity: 0,
              x: -100,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            transition={{
              duration: 1,
            }}
          />
          <div className="space-y-2.5 w-full md:w-fit">
            <div className="rounded-xl w-full md:w-[349px] lg:w-[447px] xl:w-[313px] h-[268px] p-6 bg-[#045B91] relative">
              <div
                className="absolute inset-0 h-full w-full mix-blend-overlay opacity-[0.4]"
                style={{
                  background: `url(${noise.src}) lightgray 0% 0% / 60.90909242630005px 60.90909242630005px repeat`,
                }}
              />
              <Image
                src={aboutHeroTopCardShape.src}
                alt="top shape"
                width={403}
                height={374}
                className="absolute w-full h-auto top-[-20%] left-0 pointer-events-none select-none"
              />
              <div className="relative w-full h-full">
                <div className="absolute top-0 right-0">
                  <PrismicNextImage field={slice.primary.top_card_icon} />
                </div>
                <div className="absolute bottom-0 left-0">
                  <div
                    ref={topCardValueRef}
                    className="text-[#EBF7FF] text-ellipsis font-archivo text-4xl font-bold leading-12"
                  >
                    {slice.primary.top_card_value}+
                  </div>
                  <div className="text-[#EBF7FF] font-merriweather text-xl font-normal leading-8">
                    {slice.primary.top_card_label}
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-xl w-full md:w-[349px] lg:w-[447px] xl:w-[313px] h-[230px] p-6 bg-[#045B91] relative">
              <div
                className="absolute inset-0 h-full w-full mix-blend-overlay opacity-[0.4]"
                style={{
                  background: `url(${noise.src}) lightgray 0% 0% / 60.90909242630005px 60.90909242630005px repeat`,
                }}
              />
              <Image
                src={aboutHeroBottomCardShape.src}
                alt="bottom shape"
                width={403}
                height={374}
                className="absolute w-full h-auto top-[-20%] left-[-5%] pointer-events-none select-none"
              />
              <div className="relative w-full h-full">
                <div className="absolute top-0 right-0">
                  <PrismicNextImage field={slice.primary.bottom_card_icon} />
                </div>
                <div className="absolute bottom-0 left-0">
                  <div
                    ref={bottomCardValueRef}
                    className="text-[#EBF7FF] text-ellipsis font-archivo text-4xl font-bold leading-12"
                  >
                    {slice.primary.bottom_card_value}+
                  </div>
                  <div className="text-[#EBF7FF] font-merriweather text-xl font-normal leading-8">
                    {slice.primary.bottom_card_label}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <MotionPrismicNextImage
            field={slice.primary.right_image}
            width={313}
            height={508}
            className="rounded-xl object-top-left object-cover flex-1 w-full h-[508px]"
            initial={{
              opacity: 0,
              x: 100,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            transition={{
              duration: 1,
            }}
          />
        </div>
      </Bounded>
    </section>
  );
};

export default AboutHeroSection;
